// Catch the +++ : a tiny canvas arcade game. Catch falling + marks, dodge the x marks.
const W = 320;
const H = 240;
const CELL = 4;
const STORAGE_KEY = 'treverness-arcade-high';

class SArcadeGame extends HTMLElement {
  #abort = null;
  #raf = 0;
  #announceRaf = 0;
  #ctx = null;
  #state = 'idle';
  #keys = new Set();
  #items = [];
  #playerX = W / 2;
  #score = 0;
  #lives = 3;
  #high = 0;
  #spawnTimer = 0;
  #last = 0;
  #colors = { bg: '#000', fg: '#fff', accent: '#f00' };

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.canvas = this.querySelector('canvas');
    this.#ctx = this.canvas.getContext('2d');
    this.scoreEl = this.querySelector('[data-score]');
    this.highEl = this.querySelector('[data-high]');
    this.livesEl = this.querySelector('[data-lives]');
    this.announcer = this.querySelector('[data-announcer]');
    this.startButton = this.querySelector('[data-start]');

    try {
      this.#high = Number(localStorage.getItem(STORAGE_KEY)) || 0;
    } catch {
      this.#high = 0;
    }

    const styles = getComputedStyle(this);
    this.#colors = {
      bg: styles.getPropertyValue('--color-on-surface').trim() || '#0d0e10',
      fg: styles.getPropertyValue('--color-surface').trim() || '#fbfbfa',
      accent: styles.getPropertyValue('--color-accent').trim() || '#c8201a',
    };

    this.addEventListener('command', (event) => {
      if (event.command === '--start') this.#start();
      if (event.command === '--pause') this.#togglePause();
    }, { signal });

    // Hold-to-move touch controls.
    this.querySelectorAll('[data-dir]').forEach((button) => {
      const dir = button.dataset.dir;
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.#keys.add(dir);
      }, { signal });
      ['pointerup', 'pointerleave', 'pointercancel'].forEach((type) => {
        button.addEventListener(type, () => this.#keys.delete(dir), { signal });
      });
    });

    // Drag or hover on the screen steers the catcher.
    this.canvas.addEventListener('pointermove', (event) => {
      if (this.#state !== 'playing') return;
      const rect = this.canvas.getBoundingClientRect();
      this.#playerX = ((event.clientX - rect.left) / rect.width) * W;
    }, { signal });

    this.addEventListener('keydown', (event) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowleft' || key === 'a') this.#keys.add('left');
      else if (key === 'arrowright' || key === 'd') this.#keys.add('right');
      else if (key === ' ' && event.target === this.canvas) {
        if (this.#state === 'playing' || this.#state === 'paused') this.#togglePause();
        else this.#start();
      } else return;
      if (this.#state === 'playing' || event.target === this.canvas) event.preventDefault();
    }, { signal });

    this.addEventListener('keyup', (event) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowleft' || key === 'a') this.#keys.delete('left');
      if (key === 'arrowright' || key === 'd') this.#keys.delete('right');
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.#state === 'playing') this.#togglePause();
    }, { signal });

    this.#updateHud();
    this.#drawScreen(this.dataset.labelStart);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#raf);
    cancelAnimationFrame(this.#announceRaf);
  }

  #start() {
    cancelAnimationFrame(this.#raf);
    this.#items = [];
    this.#score = 0;
    this.#lives = 3;
    this.#playerX = W / 2;
    this.#spawnTimer = 0;
    this.#state = 'playing';
    this.#updateHud();
    this.canvas.focus({ preventScroll: true });
    this.#announce(this.dataset.labelStarted);
    this.#last = performance.now();
    this.#raf = requestAnimationFrame((t) => this.#loop(t));
  }

  #togglePause() {
    if (this.#state === 'playing') {
      this.#state = 'paused';
      cancelAnimationFrame(this.#raf);
      this.#drawScreen(this.dataset.labelPaused);
      this.#announce(this.dataset.labelPaused);
    } else if (this.#state === 'paused') {
      this.#state = 'playing';
      this.#last = performance.now();
      this.#raf = requestAnimationFrame((t) => this.#loop(t));
    }
  }

  #loop(now) {
    const dt = Math.min((now - this.#last) / 1000, 0.05);
    this.#last = now;
    this.#update(dt);
    if (this.#state !== 'playing') return;
    this.#render();
    this.#raf = requestAnimationFrame((t) => this.#loop(t));
  }

  #update(dt) {
    const speed = 180;
    if (this.#keys.has('left')) this.#playerX -= speed * dt;
    if (this.#keys.has('right')) this.#playerX += speed * dt;
    this.#playerX = Math.max(20, Math.min(W - 20, this.#playerX));

    const level = 1 + Math.floor(this.#score / 5);
    this.#spawnTimer -= dt;
    if (this.#spawnTimer <= 0) {
      const bad = Math.random() < Math.min(0.25 + level * 0.04, 0.5);
      this.#items.push({ x: 12 + Math.random() * (W - 24), y: -12, bad, vy: 50 + level * 12 + Math.random() * 30 });
      this.#spawnTimer = Math.max(0.35, 1 - level * 0.07);
    }

    const catcherY = H - 18;
    this.#items = this.#items.filter((item) => {
      item.y += item.vy * dt;
      const caught = item.y > catcherY - 8 && item.y < catcherY + 6 && Math.abs(item.x - this.#playerX) < 24;
      if (caught) {
        if (item.bad) this.#lives -= 1;
        else this.#score += 1;
        this.#updateHud();
        return false;
      }
      if (item.y > H + 12) {
        return false;
      }
      return true;
    });

    if (this.#lives <= 0) this.#gameOver();
  }

  #gameOver() {
    this.#state = 'over';
    cancelAnimationFrame(this.#raf);
    if (this.#score > this.#high) {
      this.#high = this.#score;
      try {
        localStorage.setItem(STORAGE_KEY, String(this.#high));
      } catch {
        // Storage can be unavailable in private browsing; the high score just won't persist.
      }
    }
    this.#updateHud();
    this.#drawScreen(this.dataset.labelOver, String(this.#score));
    this.#announce((this.dataset.labelOverAnnouncement || '').replace('[score]', String(this.#score)));
  }

  #updateHud() {
    if (this.scoreEl) this.scoreEl.textContent = String(this.#score).padStart(4, '0');
    if (this.highEl) this.highEl.textContent = String(Math.max(this.#high, this.#score)).padStart(4, '0');
    if (this.livesEl) this.livesEl.textContent = '+'.repeat(Math.max(this.#lives, 0));
  }

  #announce(text) {
    if (!this.announcer || !text) return;
    cancelAnimationFrame(this.#announceRaf);
    this.announcer.textContent = '';
    this.#announceRaf = requestAnimationFrame(() => {
      this.announcer.textContent = text;
    });
  }

  #drawMark(x, y, bad, color) {
    const ctx = this.#ctx;
    ctx.fillStyle = color;
    const cells = bad
      ? [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]]
      : [[0, -1], [-1, 0], [0, 0], [1, 0], [0, 1]];
    cells.forEach(([cx, cy]) => {
      ctx.fillRect(Math.round(x + cx * CELL * 1.5 - CELL * 0.75), Math.round(y + cy * CELL * 1.5 - CELL * 0.75), CELL * 1.5, CELL * 1.5);
    });
  }

  #render() {
    const ctx = this.#ctx;
    ctx.fillStyle = this.#colors.bg;
    ctx.fillRect(0, 0, W, H);

    // Ground line.
    ctx.fillStyle = this.#colors.fg;
    ctx.globalAlpha = 0.25;
    ctx.fillRect(0, H - 6, W, 2);
    ctx.globalAlpha = 1;

    this.#items.forEach((item) => this.#drawMark(item.x, item.y, item.bad, item.bad ? this.#colors.accent : this.#colors.fg));

    // Catcher: a pixel tray.
    const y = H - 18;
    ctx.fillStyle = this.#colors.fg;
    ctx.fillRect(Math.round(this.#playerX - 22), y, 44, 4);
    ctx.fillRect(Math.round(this.#playerX - 22), y - 6, 4, 6);
    ctx.fillRect(Math.round(this.#playerX + 18), y - 6, 4, 6);
  }

  #drawScreen(title, subtitle) {
    const ctx = this.#ctx;
    ctx.fillStyle = this.#colors.bg;
    ctx.fillRect(0, 0, W, H);
    this.#drawMark(W / 2 - 24, 70, false, this.#colors.fg);
    this.#drawMark(W / 2, 70, false, this.#colors.accent);
    this.#drawMark(W / 2 + 24, 70, false, this.#colors.fg);
    ctx.fillStyle = this.#colors.fg;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 18px ui-monospace, monospace';
    ctx.fillText(title || '', W / 2, 130);
    if (subtitle) {
      ctx.font = 'bold 28px ui-monospace, monospace';
      ctx.fillText(subtitle, W / 2, 165);
    }
  }
}

if (!customElements.get('s-arcade-game')) customElements.define('s-arcade-game', SArcadeGame);
